import { useCallback, useEffect, useState } from "react";
import { FileText, GripVertical, Loader2, Printer, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Loading, PageHeader, Panel, PrimaryButtonClass } from "@/components/common/Chrome";
import { Pill, RangeSelect } from "@/components/dash/DashKit";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime, money, num } from "@/lib/format";

export default function ImpactReport() {
  const { can } = useAuth();
  const [history, setHistory] = useState(null);
  const [report, setReport] = useState(null);
  const [range, setRange] = useState("6M");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/ai/impact-reports");
    setHistory(data);
    if (data.length && !report) setReport(data[0]);
  }, [report]);

  useEffect(() => { load().catch(() => setHistory([])); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/impact-report", { range });
      setReport(data);
      toast.success(`${data.report_id} generated${data.narrative_source === "gpt-5.5" ? " with GPT-5.5" : ""}`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  if (!history) return <Loading label="Loading impact reports" />;

  const f = report?.facts;
  const n = report?.narrative;

  return (
    <div className="space-y-5">
      <div className="no-print">
        <PageHeader
          eyebrow="SCR-021 · Impact & Accountability"
          title="A board-ready report in one click"
          description="Programme delivery, restricted-fund discipline and regulatory posture are pulled straight from the ledger and the rule base, then written up for trustees. Nothing is typed twice and no figure is invented."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <RangeSelect value={range} onChange={setRange} testId="report-range" />
              {can("program", "read") && (
                <Button onClick={generate} disabled={busy} data-testid="generate-report-button"
                        className={PrimaryButtonClass}>
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
                  Generate report
                </Button>
              )}
              {report && (
                <Button variant="outline" onClick={() => window.print()} data-testid="print-report-button"
                        className="rounded-full">
                  <Printer className="mr-1.5 h-4 w-4" /> Print / PDF
                </Button>
              )}
            </div>
          }
        />
      </div>

      {!report && (
        <Panel testId="panel-no-report" kicker="Nothing yet" title="No report generated">
          <p className="p-4 text-[12.5px] text-muted-foreground">
            Choose a period and press <strong>Generate report</strong>. The narrative is drafted by
            GPT-5.5 from your own figures, with a computed fallback if the model is unavailable.
          </p>
        </Panel>
      )}

      {report && (
        <>
          <article className="panel print-area space-y-6 p-6 md:p-8" data-testid="impact-report-document">
            <header className="border-b border-border pb-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="kicker">{report.report_id} · {f.period_label}</span>
                <Pill tone={report.narrative_source === "gpt-5.5" ? "primary" : "muted"}
                      testId="report-source-pill">
                  {report.narrative_source === "gpt-5.5" ? "Drafted with GPT-5.5" : "Computed narrative"}
                </Pill>
              </div>
              <h2 className="mt-2 font-display text-[24px] font-bold tracking-tight text-foreground">
                {report.title}
              </h2>
              <p className="mt-2 text-[14px] font-medium text-primary" data-testid="report-headline">
                {n.headline}
              </p>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Prepared for the Board of Trustees · ServeCOR Demo Foundation · generated by{" "}
                {report.generated_by_name || report.generated_by} on {dateTime(report.generated_at)}
              </p>
            </header>

            <section className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="report-figures">
              {[
                ["People reached", num(f.total_beneficiaries)],
                ["Donations received", money(f.donations_total, "INR", true)],
                ["Charitable expenditure", money(f.expenditure, "INR", true)],
                ["Programme spend ratio", `${f.programme_spend_ratio}%`],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-border p-3">
                  <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
                  <div className="mt-1 font-display text-[19px] font-bold text-foreground">{value}</div>
                </div>
              ))}
            </section>

            <Section title="Executive summary" testId="report-exec-summary">
              {n.executive_summary.split("\n\n").map((p, i) => (
                <p key={i} className="mb-2 text-[13px] leading-relaxed text-foreground/85">{p}</p>
              ))}
            </Section>

            <Section title="Programme delivery" testId="report-programmes">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-y border-border bg-muted/40">
                      {["Programme", "Budget", "Spent", "Utilisation", "Beneficiaries"].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {f.programmes.map((p) => (
                      <tr key={p.code} data-testid={`report-programme-${p.code}`}>
                        <td className="px-3 py-2 text-[12.5px] text-foreground">{p.name}
                          <span className="ml-1 font-mono text-[10px] text-muted-foreground">{p.code}</span>
                        </td>
                        <td className="px-3 py-2 text-[12.5px] text-foreground">{money(p.budget, "INR", true)}</td>
                        <td className="px-3 py-2 text-[12.5px] text-foreground">{money(p.spent, "INR", true)}</td>
                        <td className="px-3 py-2 text-[12.5px] text-foreground">{p.utilisation}%</td>
                        <td className="px-3 py-2 text-[12.5px] text-foreground">{num(p.beneficiaries)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 space-y-2">
                {(n.programme_commentary || []).map((c) => (
                  <p key={c.code} className="text-[13px] leading-relaxed text-foreground/85">
                    <span className="font-mono text-[11px] text-primary">{c.code}</span> — {c.commentary}
                  </p>
                ))}
              </div>
            </Section>

            <Section title="Financial position" testId="report-financial">
              <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
                {[
                  ["Income", money(f.income, "INR", true)],
                  ["Expenditure", money(f.expenditure, "INR", true)],
                  ["Movement in funds", money(f.surplus, "INR", true)],
                  ["Restricted funds", num(f.restricted_funds)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg bg-muted/50 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
                    <div className="text-[13.5px] font-semibold text-foreground">{value}</div>
                  </div>
                ))}
              </div>
              <p className="text-[13px] leading-relaxed text-foreground/85">{n.financial_commentary}</p>
            </Section>

            <Section title="Regulatory posture" testId="report-compliance">
              <p className="text-[13px] leading-relaxed text-foreground/85">{n.compliance_commentary}</p>
              <p className="mt-2 text-[11.5px] text-muted-foreground">
                {f.rules_published} of {f.rules_total} rule versions published across {f.jurisdictions}{" "}
                jurisdictions · {f.rules_evidenced} independently evidenced · {num(f.beneficiary_records)}{" "}
                beneficiary records held under consent.
              </p>
            </Section>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <Section title="Principal risks" testId="report-risks">
                <ul className="space-y-1.5">
                  {(n.risks || []).map((r, i) => (
                    <li key={i} className="flex gap-2 text-[13px] text-foreground/85">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" />{r}
                    </li>
                  ))}
                </ul>
              </Section>
              <Section title="Recommendations to the board" testId="report-recommendations">
                <ul className="space-y-1.5">
                  {(n.recommendations || []).map((r, i) => (
                    <li key={i} className="flex gap-2 text-[13px] text-foreground/85">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--teal))]" />{r}
                    </li>
                  ))}
                </ul>
              </Section>
            </div>
          </article>

          <Panel testId="panel-report-history" kicker="Archive" title="Previously generated reports"
                 className="no-print">
            <div className="divide-y divide-border">
              {history.map((h) => (
                <button key={h.id} onClick={() => setReport(h)}
                        data-testid={`report-history-${h.report_id}`}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-muted/60">
                  <GripVertical className="hidden h-3 w-3" />
                  <FileText className="h-3.5 w-3.5 text-primary" />
                  <span className="font-mono text-[11px] text-muted-foreground">{h.report_id}</span>
                  <span className="flex-1 truncate text-[12.5px] text-foreground">{h.title}</span>
                  <span className="text-[11px] text-muted-foreground">{h.range}</span>
                  <span className="text-[11px] text-muted-foreground">{dateTime(h.generated_at)}</span>
                </button>
              ))}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

const Section = ({ title, children, testId }) => (
  <section data-testid={testId}>
    <h3 className="mb-2 font-display text-[15px] font-semibold text-foreground">{title}</h3>
    {children}
  </section>
);
